import { Button } from "@/components/ui/button";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { ChevronDown, Download, Loader2, RefreshCw, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface LandscapeBulkBarProps {
  count: number;
  isBulkDeleting: boolean;
  isBulkRefreshing: boolean;
  isExporting: boolean;
  bulkDeleteOpen: boolean;
  onBulkDeleteOpenChange: (open: boolean) => void;
  onClear: () => void;
  onExport: () => void;
  onRefresh: () => void;
  onDelete: () => void;
}

export function LandscapeBulkBar({
  count,
  isBulkDeleting,
  isBulkRefreshing,
  isExporting,
  bulkDeleteOpen,
  onBulkDeleteOpenChange,
  onClear,
  onExport,
  onRefresh,
  onDelete,
}: LandscapeBulkBarProps) {
  if (count <= 0) return null;

  return (
    <BulkActionBar
      count={count}
      onClear={onClear}
      entityLabel="landscape"
      className="mb-2"
      sticky
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-7 gap-1"
            disabled={isBulkDeleting}
          >
            Bulk Actions
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="bg-popover z-50">
          <DropdownMenuItem onClick={onExport} disabled={isExporting}>
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Export Selected
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onRefresh} disabled={isBulkRefreshing}>
            {isBulkRefreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh Selected
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => onBulkDeleteOpenChange(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Selected
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={onBulkDeleteOpenChange}
        title="Delete Selected Landscapes"
        description={`Are you sure you want to delete ${count} landscape${count !== 1 ? "s" : ""}? This action cannot be undone.`}
        onConfirm={onDelete}
      />
    </BulkActionBar>
  );
}
